import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import ServiceModule from '@modules/ServiceModule';
import {
  phoneState,
  phoneStateHasCallEstablished,
} from '@modules/SoftPhoneModule/state';
import { UserActivityStatus } from '@api/generated/graphql';
import IAppLogger from '@app/lib/IAppLogger';
import TasksModule from '@modules/TasksModule';
import IAppEvents from '@app/lib/IAppEvents';
import { UserStatusEventType } from '@modules/UserStatusModule/types';
import FullStoryModule from '@modules/FullStoryModule';

// Export constants for testing purposes
export const DOUBLE_CHECK_DELAY_MS = 5000;
// 25-min cooldown period (5x the detection interval)
export const COOLDOWN_PERIOD_MS = 1_500_000;

interface StuckStatusState {
  detectionTime: number | null;
  hasEmitted: boolean;
  checkIntervalId: NodeJS.Timeout | null;
  doubleCheckTimeoutId: NodeJS.Timeout | null;
}

export class StuckStatusDetector {
  private state: StuckStatusState = {
    detectionTime: null,
    hasEmitted: false,
    checkIntervalId: null,
    doubleCheckTimeoutId: null,
  };

  // Allow timer values to be injected for testing
  private readonly doubleCheckDelayMs: number;
  private readonly cooldownPeriodMs: number;

  constructor(
    private readonly events: IAppEvents,
    private readonly appLogger: IAppLogger,
    config?: {
      doubleCheckDelayMs?: number;
      cooldownPeriodMs?: number;
    },
  ) {
    this.doubleCheckDelayMs =
      config?.doubleCheckDelayMs ?? DOUBLE_CHECK_DELAY_MS;
    this.cooldownPeriodMs = config?.cooldownPeriodMs ?? COOLDOWN_PERIOD_MS;
  }

  /**
   * Check if user is in a stuck state
   * Stuck = Activity shows call BUT phone has no active call
   * NOT stuck if call was accepted on mobile device
   */
  private isUserStuck(): boolean {
    const currentUser = ServiceModule.getCurrentUser();

    // Stack Status it's not possible to detect if:
    // - current user doesn't exist
    // - phone is not connected
    // - call answered via mobile app
    if (
      !currentUser ||
      !phoneState.phoneConnected ||
      TasksModule.hasActiveTaskAcceptedByMobile()
    ) {
      return false;
    }

    const activityStatus = currentUser.activityStatus;
    const hasActiveCall = phoneStateHasCallEstablished();

    const isActivityInCallState =
      activityStatus === UserActivityStatus.OnACall ||
      activityStatus === UserActivityStatus.OnExternalCall ||
      activityStatus === UserActivityStatus.CallOffer;

    return isActivityInCallState && !hasActiveCall;
  }

  /**
   * Start periodic stuck status detection
   */
  start(): void {
    this.stop(); // Clear any existing timers

    const { value: intervalMs } = FeatureFlagModule.getFeatureFlagValue(
      FeatureFlagKeys.featureLogUserStatusStuckInterval,
    );

    this.appLogger.info('[StuckStatusDetector]', { intervalMs });

    // Stuck status detection service should be disabled when:
    // - interval <= 0
    if (typeof intervalMs !== 'number' || intervalMs <= 0) {
      this.appLogger.info('[StuckStatusDetector] Disabled (interval <= 0)');
      return;
    }

    this.appLogger.info(
      `[StuckStatusDetector] Started with interval: ${intervalMs}ms`,
    );

    this.state.checkIntervalId = setInterval(() => {
      this.periodicCheck();
    }, intervalMs);
  }

  /**
   * Perform periodic stuck status check with cooldown logic
   */
  private periodicCheck(): void {
    const now = Date.now();

    // Check if user is stuck
    if (this.isUserStuck()) {
      // If we've already detected and emitted, check cooldown
      if (this.state.detectionTime !== null) {
        if (this.state.hasEmitted) {
          // Already emitted, check if cooldown has passed
          const timeSinceDetection = now - this.state.detectionTime;
          if (timeSinceDetection < this.cooldownPeriodMs) {
            // Still in cooldown, do nothing
            return;
          }
          // Cooldown passed, reset and start new detection
          this.reset();
        }
      }

      // First detection or cooldown passed - start double-check
      if (this.state.detectionTime === null) {
        this.state.detectionTime = now;
        this.appLogger.info(
          '[StuckStatusDetector] Potentially stuck detected, scheduling double-check...',
        );

        // Schedule double-check after configured delay
        this.state.doubleCheckTimeoutId = setTimeout(() => {
          this.doubleCheckAndEmit();
        }, this.doubleCheckDelayMs);
      }
    } else {
      // User is not stuck - reset if we were tracking
      if (this.state.detectionTime !== null) {
        this.appLogger.info(
          '[StuckStatusDetector] User no longer stuck, resetting state',
        );
        this.reset();
      }
    }
  }

  /**
   * Double-check if user is still stuck, then emit event
   */
  private doubleCheckAndEmit(): void {
    if (this.isUserStuck()) {
      this.appLogger.info(
        '[StuckStatusDetector] Double-check confirmed stuck status, emitting event',
      );
      this.emitStuckEvent();
      this.state.hasEmitted = true;
    } else {
      this.appLogger.info(
        '[StuckStatusDetector] Double-check: user no longer stuck',
      );
      this.reset();
    }
  }

  /**
   * Emit FullStory event for stuck status
   */
  private emitStuckEvent(): void {
    const currentUser = ServiceModule.getCurrentUser();
    if (!currentUser) {
      return;
    }

    this.appLogger.error('[StuckStatusDetector]', {
      name: 'User Status Stuck',
      properties: {
        activityStatus: currentUser.activityStatus,
        availabilityStatus: currentUser.availabilityStatus,
        timestamp: new Date().toISOString(),
      },
    });

    FullStoryModule.trackEvent('[CCP] Stuck Status Detected', {
      name: 'User Status Stuck',
      properties: {
        activityStatus: currentUser.activityStatus,
        availabilityStatus: currentUser.availabilityStatus,
        timestamp: new Date().toISOString(),
      },
    });

    if (
      FeatureFlagModule.isEnabled(FeatureFlagKeys.featureAutoRefreshUserStatus)
    ) {
      this.events.emit(UserStatusEventType.USER_STATUS_STUCK);
    }
  }

  /**
   * Reset detection state completely
   */
  private reset(): void {
    if (this.state.doubleCheckTimeoutId) {
      clearTimeout(this.state.doubleCheckTimeoutId);
      this.state.doubleCheckTimeoutId = null;
    }
    this.state.detectionTime = null;
    this.state.hasEmitted = false;
  }

  /**
   * Stop the detector and clear all timers
   */
  stop(): void {
    if (this.state.checkIntervalId) {
      clearInterval(this.state.checkIntervalId);
      this.state.checkIntervalId = null;
    }

    if (this.state.doubleCheckTimeoutId) {
      clearTimeout(this.state.doubleCheckTimeoutId);
      this.state.doubleCheckTimeoutId = null;
    }

    this.appLogger.info('[StuckStatusDetector] Stopped');
  }

  /**
   * Get current detector state (for debugging)
   */
  getState(): Readonly<StuckStatusState> {
    return { ...this.state };
  }
}
